import { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { AxiosErrorCodeString, AxiosErrorHandler, AxiosErrorHandlerMap, AxiosInterceptorConfig, AxiosLogger } from './types';

const HTTP_MUTATION_METHODS = ['delete', 'patch', 'post', 'put'] as const;

export class AxiosInterceptorManager {
  private static requestAbortControllers = new Map<string, AbortController>();

  static apply(axiosInstance: AxiosInstance, config: AxiosInterceptorConfig): AxiosInstance {
    AxiosInterceptorManager.setupRequestInterceptor(axiosInstance, config);
    AxiosInterceptorManager.setupResponseInterceptor(axiosInstance, config);

    return axiosInstance;
  }

  private static async executeErrorHandler(
    errorHandlerMap: AxiosErrorHandlerMap,
    error: AxiosError | undefined,
    logger: AxiosLogger | undefined,
  ): Promise<void> {
    if (!error) {
      return;
    }

    let errorHandler: AxiosErrorHandler | undefined;

    if (error.code) {
      errorHandler = errorHandlerMap[error.code as AxiosErrorCodeString];
    }
    if (!errorHandler && error.response) {
      errorHandler = errorHandlerMap[error.response?.status as never as AxiosErrorCodeString];
    }

    if (errorHandler) {
      try {
        await errorHandler();
      } catch (handlerExecutionError) {
        if (logger) {
          await logger.error(handlerExecutionError as Error);
        }
      }
    }
  }

  private static generateRequestCacheKey(config: InternalAxiosRequestConfig): string {
    return `${config.method}-${config.url}-${JSON.stringify(config.params)}-${JSON.stringify(config.data)}`;
  }

  private static async handleRequestError(
    errorHandlerMap: AxiosErrorHandlerMap,
    logger: AxiosLogger | undefined,
    error: AxiosError | undefined,
  ): Promise<never> {
    if (logger) {
      await logger.error(error || new AxiosError('Unknown error'));
    }

    if (error) {
      // 1) mark as potentially uncaught
      (error as any).__uncaught = true;

      // 2) schedule a check in the next tick
      setTimeout(() => {
        if ((error as any).__uncaught) {
          AxiosInterceptorManager.executeErrorHandler(errorHandlerMap, error, logger);
        } else {
          console.debug('Error was caught and handled before uncaught check')
        }
      }, 0);
    }

    return Promise.reject(error ?? new AxiosError('Unknown error'));
  }

  private static setupRequestInterceptor(
    axiosInstance: AxiosInstance,
    { logger, errorHandlerMap, requestTransformer, enableCancellation, mutationTimeout = 60000 }: AxiosInterceptorConfig,
  ): void {
    axiosInstance.interceptors.request.use(
      async (request: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        if (logger) {
          await logger.request(request);
        }

        // Apply the request transformer function if provided
        if (requestTransformer) {
          await requestTransformer(request);
        }

        if (HTTP_MUTATION_METHODS.includes(request.method as never) && request.timeout === undefined) {
          request.timeout = mutationTimeout;
        }

        if (enableCancellation) {
          const cacheKey = AxiosInterceptorManager.generateRequestCacheKey(request);
          const existingAbortController = AxiosInterceptorManager.requestAbortControllers.get(cacheKey);

          if (existingAbortController) {
            existingAbortController.abort();
            AxiosInterceptorManager.requestAbortControllers.delete(cacheKey);
          }

          const abortController = new AbortController();
          AxiosInterceptorManager.requestAbortControllers.set(cacheKey, abortController);
          request.signal = abortController.signal;
        }

        return request;
      },
      (error) => AxiosInterceptorManager.handleRequestError(errorHandlerMap, logger, error),
    );
  }

  private static setupResponseInterceptor(
    axiosInstance: AxiosInstance,
    { logger, errorHandlerMap }: AxiosInterceptorConfig,
  ): void {
    axiosInstance.interceptors.response.use(
      async (response: AxiosResponse) => {
        if (logger) {
          await logger.response(response);
        }

        if (response.config && AxiosInterceptorManager.requestAbortControllers.has(AxiosInterceptorManager.generateRequestCacheKey(response.config))) {
          AxiosInterceptorManager.requestAbortControllers.delete(AxiosInterceptorManager.generateRequestCacheKey(response.config));
        }

        return response;
      },
      (error) => AxiosInterceptorManager.handleRequestError(errorHandlerMap, logger, error),
    );
  }
}
